import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import { clearSessionExpiry } from '../../utils/useSessionExpiry';
import { getUploadToken, clearUploadSession, notifySessionLost } from './uploadAccess';

/**
 * Thin client for the server's /api/v1/missed-calls routes (AONE-Universe-server api/missed_calls.js,
 * docs/MISSED_CALLS_PLAN.md §5.2). Every call carries the admin JWT the same way the rest of the dashboard does.
 */
const BASE = `${process.env.REACT_APP_URL}/api/v1/missed-calls`;

/**
 * A signed-in admin's JWT always wins: the six upload endpoints accept it, and it is the right identity to act
 * under. The phone's password token (uploadAccess.js) is the fallback for where there is no cookie at all.
 *
 * Deliberately derived per request rather than from a mode flag — this module is shared with the desktop Missed
 * Calls page, and a stale flag there would send the wrong header or swallow the /signin redirect.
 */
const usingUploadToken = () => !getAuthHeader() && Boolean(getUploadToken());
const authorization = () => getAuthHeader() || (getUploadToken() ? `Bearer ${getUploadToken()}` : '');
const headers = () => ({ 'Content-Type': 'application/json', Authorization: authorization() });

// Expired session → back to sign-in, the same way the dashboard layout does it. What a 401 means depends on what
// the request actually carried, so each caller passes that in rather than asking a shared flag afterwards.
const onError = (sentUploadToken) => (err) => {
    if (err?.response?.status === 401) {
        if (sentUploadToken) {
            // Wrong, expired, or superseded token (the password was rotated): drop it and tell the gate, so the
            // phone lands back on the password screen instead of a sign-in nobody there can use — or, worse, a
            // silent spinner when the failure happened inside the poll.
            clearUploadSession();
            notifySessionLost();
            throw err;
        }
        clearSessionExpiry();
        window.location.replace('/signin');
    }
    throw err;
};
const get = (path, params) => Axios.get(`${BASE}${path}`, { headers: headers(), params }).then((r) => r.data, onError(usingUploadToken()));
const post = (path, data) => Axios.post(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError(usingUploadToken()));
const patch = (path, data) => Axios.patch(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError(usingUploadToken()));
const put = (path, data) => Axios.put(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError(usingUploadToken()));
const del = (path) => Axios.delete(`${BASE}${path}`, { headers: headers() }).then((r) => r.data, onError(usingUploadToken()));

export const missedCallsApi = {
    /** { today, sms_default, sms_max, text_max, max_screenshots, max_screenshot_mb } */
    config: () => get('/config'),
    /** { rows, next_cursor } — newest day first, 번호 ascending */
    list: (params) => get('', params),
    /** { row } */
    get: (id) => get(`/${id}`),
    /** { row } — note and / or task_progress (담당자 연결 is setHandoff) */
    update: (id, body) => patch(`/${id}`, body),
    /** { admins } — every admin's id, uuid and name (no emails): the 담당자 연결 menu and a manager's 담당 지정 */
    admins: () => get('/admins'),
    /** { row } — 담당자 연결 = that admin, or null to clear (any admin) */
    setHandoff: (id, adminId) => put(`/${id}/handoff`, { admin_id: adminId ?? null }),
    /** 담당자 = me, or `adminId` when a voice manager puts someone else on the row */
    claim: (id, adminId) => post(`/${id}/claim`, adminId ? { admin_id: adminId } : {}),
    unclaim: (id) => del(`/${id}/claim`),
    setHandled: (id, isHandled) => put(`/${id}/handled`, { is_handled: isHandled }),
    /** { sent, reason?, redirected_to?, row } */
    sendSms: (id, message) => post(`/${id}/sms`, { message }),
    /** { list_date, candidates } — what saving these rows would do, without doing it */
    previewRows: (rows, listDate) => post('/rows/preview', { rows, ...(listDate ? { list_date: listDate } : {}) }),
    /** { created, merged, skipped, rows } — rows an admin typed in rather than uploaded */
    addRows: (rows, listDate) => post('/rows', { rows, ...(listDate ? { list_date: listDate } : {}) }),
    /** { students, teachers } — names only, for the Who cell (any admin; 2+ characters) */
    // by 'phone': all or part of a number on file (4+ digits); names come back with which number matched, never the number
    searchContacts: (q, by = 'name') => get('/contacts', by === 'phone' ? { q, by } : { q }),
    /** { row } — who this number is: { student_uuid }, { student_uuids } (primary first), { teacher_uuid }, or {} for lookup */
    setWho: (id, body) => put(`/${id}/who`, body),
    /** { row } — persist the per-cell Who text color. */
    setWhoColor: (id, isRed) => put(`/${id}/who-color`, { is_red: isRed }),
    /** { events } — oldest first */
    events: (id) => get(`/${id}/events`),
    remove: (id) => del(`/${id}`),
    /** { days, row_number, calls } — calls not tied to any row yet; `number_matches` flags the ones that differ. */
    attachableCalls: (id, days) => get(`/${id}/attachable-calls`, days ? { days } : undefined),
    /** { row, attached_student } — tie an already-placed call to the row (통화 연결). */
    attachCall: (id, callLogId, { move = false, attachStudent = false } = {}) =>
        post(`/${id}/calls`, { call_log_id: callLogId, move, attach_student: attachStudent }),
    /** { row } — 연결 해제: clears the link only; the call and its memo are left alone. */
    detachCall: (id, callLogId) => del(`/${id}/calls/${callLogId}`),
    /** { row } — correct the 부재 time ("HH:mm"), or null for 시간 모름. Never reopens a 완료 row. */
    setMissedTime: (id, missedTime) => put(`/${id}/missed-time`, { missed_time: missedTime ?? null }),
    /** { row } — 복구: put a soft-deleted row back. Any admin, the same as deleting. 409 if it is not deleted. */
    restore: (id) => post(`/${id}/restore`, {}),
    /** The screenshots, confirmed as today's → { import } (202). `onProgress(0..1)` while uploading. */
    upload: (files, onProgress, listDates) => {
        const form = new FormData();
        form.append('confirmed_today', 'true');
        // One field per ticked day (plan §3.9): multipart repeats it, and the server reads the whole set. Absent
        // means today. A single date is still accepted, so older callers keep working.
        const days = Array.isArray(listDates) ? listDates : (listDates ? [listDates] : []);
        days.forEach((d) => form.append('list_date', d));
        files.forEach((file) => form.append('screenshots', file));
        return Axios.post(`${BASE}/imports`, form, {
            headers: { Authorization: authorization() }, // the browser sets the multipart boundary
            onUploadProgress: onProgress ? (e) => onProgress(e.total ? e.loaded / e.total : 0) : undefined,
        }).then((r) => r.data, onError(usingUploadToken()));
    },
    /** { import } — status, candidates once READY, 5-minute screenshot links */
    getImport: (id) => get(`/imports/${id}`),
    /** { import_id, created, merged, rows } */
    confirmImport: (id, rows) => post(`/imports/${id}/confirm`, { rows }),
    discardImport: (id) => post(`/imports/${id}/discard`, {}),
    retryImport: (id) => post(`/imports/${id}/retry`, {}),
};

export const errorMessage = (err) =>
    err?.response?.data?.error || err?.message || 'Unknown error';

export default missedCallsApi;
